import React from "react";
import "./PaymentAdvanceHistoryModal.css";
import {
  computeSalaryMonth,
  formatFrDate,
  formatMonthLabel,
  money,
  monthKeyFromDate,
} from "../utils/salaryMonth";

const PaymentAdvanceHistoryModal = ({ driver, advances, payments, absences, month, onEditAdvance, onDeleteAdvance, onEditPayment, onDeletePayment, onDeleteAbsence, onClose }) => {
  const inMonth = (value) => monthKeyFromDate(value) === month;
  const filteredAdvances = advances.filter((a) => inMonth(a.advance_date));
  const filteredPayments = payments.filter((p) => inMonth(p.payment_date));
  const filteredAbsences = absences.filter((a) => inMonth(a.absence_date));

  const history = [
    ...filteredAdvances.map((advance) => ({
      id: advance.id,
      type: "Avance",
      sortDate: monthKeyFromDate(advance.advance_date) ? String(advance.advance_date).slice(0, 10) : "",
      date: formatFrDate(advance.advance_date),
      amount: money(advance.amount).toFixed(2),
      description: advance.description || "N/A",
      payment_method:
        advance.payment_method === "espèce"
          ? "Espèce"
          : advance.payment_method === "virement_bancaire"
          ? "Par virement bancaire"
          : "N/A",
      numero_de_virement: advance.numero_de_virement || "N/A",
      status: null,
      original: advance,
    })),
    ...filteredPayments.map((payment) => ({
      id: payment.id,
      type: "Paiement",
      sortDate: String(payment.payment_date || "").slice(0, 10),
      date: formatFrDate(payment.payment_date),
      amount: money(payment.salary_paid).toFixed(2),
      description: payment.description || "N/A",
      payment_method:
        payment.payment_method === "espèce"
          ? "Espèce"
          : payment.payment_method === "virement_bancaire"
          ? "Par virement bancaire"
          : "N/A",
      numero_de_virement: payment.numero_de_virement || "N/A",
      status: payment.status || "N/A",
      original: payment,
    })),
    ...filteredAbsences.map((absence) => ({
      id: absence.id,
      type: "Absence",
      sortDate: String(absence.absence_date || "").slice(0, 10),
      date: formatFrDate(absence.absence_date),
      amount: money(absence.deduction_amount).toFixed(2),
      description: `Absence de ${absence.days_absent} jour(s)`,
      payment_method: "N/A",
      numero_de_virement: "N/A",
      status: null,
      original: absence,
    })),
  ];

  history.sort((a, b) => (a.sortDate < b.sortDate ? 1 : a.sortDate > b.sortDate ? -1 : 0));

  const snapshot = computeSalaryMonth({
    driverId: driver.id,
    month,
    baseSalary: driver.base_salary,
    startDate: driver.start_date_iso,
    advances,
    payments,
    absences,
  });
  const totalPaid = snapshot.totalAdvances + snapshot.totalPayments;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Historique pour {driver.name} ({formatMonthLabel(month) || month})</h3>
          <button className="close-modal-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="summary-section">
          <div className="summary-item">
            <div className="summary-label">Salaire de Base:</div>
            <div className="summary-value">{money(driver.base_salary).toFixed(2)} TND</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Règlement salaire:</div>
            <div className="summary-value">{snapshot.totalPayments.toFixed(2)} TND</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">A compte:</div>
            <div className="summary-value">{snapshot.totalAdvances.toFixed(2)} TND</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Déductions (Absences):</div>
            <div className="summary-value">{snapshot.totalDeductions.toFixed(2)} TND</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Report du mois précédent:</div>
            <div className="summary-value">{snapshot.carryIn.toFixed(2)} TND</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Total Payé:</div>
            <div className="summary-value">{totalPaid.toFixed(2)} TND</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">Salaire Restant:</div>
            <div className={`summary-value ${snapshot.remaining >= 0 ? "positive" : "negative"}`}>
              {snapshot.remaining.toFixed(2)} TND
            </div>
          </div>
        </div>

        <div className="history-table">
          {history.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Montant (TND)</th>
                  <th>Description</th>
                  <th>Méthode de Paiement</th>
                  <th>Numéro de Virement</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr
                    key={`${item.type}-${item.id}`}
                    className={
                      item.type === "Avance" ? "advance-row" :
                      item.type === "Paiement" ? "payment-row" :
                      "absence-row"
                    }
                  >
                    <td>{item.date}</td>
                    <td>{item.type}</td>
                    <td>{item.amount}</td>
                    <td>{item.description}</td>
                    <td>{item.payment_method}</td>
                    <td>{item.numero_de_virement}</td>
                    <td>{item.status}</td>
                    <td>
                      {item.type === "Avance" && (
                        <button
                          className="history-edit-btn"
                          onClick={() => onEditAdvance(item.original)}
                        >
                          Modifier
                        </button>
                      )}
                      {item.type === "Paiement" && (
                        <button
                          className="history-edit-btn"
                          onClick={() => onEditPayment(item.original)}
                        >
                          Modifier
                        </button>
                      )}
                      <button
                        className="history-delete-btn"
                        onClick={() =>
                          item.type === "Avance"
                            ? onDeleteAdvance(item.id)
                            : item.type === "Paiement"
                            ? onDeletePayment(item.id)
                            : onDeleteAbsence(item.id)
                        }
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-history">Aucun historique trouvé.</div>
          )}
        </div>
        <div className="modal-footer">
          <button className="close-btn" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentAdvanceHistoryModal;
